import { z } from 'zod';
import { FeatureKey } from './features.js';
/**
 * Integrations are plug-ins described by a manifest. Adding a new one means writing
 * a manifest plus an adapter in apps/api/src/integrations/<key>; nothing else changes.
 * See docs/adr/0006-integration-framework.md.
 */
export const IntegrationCategory = z.enum(['repository', 'accounting', 'payments', 'email', 'calendar', 'import', 'automation']);
export const IntegrationAuth = z.enum(['oauth2', 'app_install', 'api_key', 'webhook_only']);
export const IntegrationCapability = z.enum([
    'repo.push',
    'repo.pull_request',
    'repo.deploy',
    'repo.commit_link',
    'invoice.sync',
    'payment.collect',
    'email.inbound',
    'calendar.events',
    'import.workspace',
]);
export const IntegrationManifest = z.object({
    key: z.string().regex(/^[a-z][a-z0-9_]*$/),
    name: z.string(),
    category: IntegrationCategory,
    auth: IntegrationAuth,
    /** The workspace feature that must be on for this integration to appear. */
    feature: FeatureKey,
    capabilities: z.array(IntegrationCapability).min(1),
    /** Inbound webhooks are verified (HMAC/signature) and replay-protected by the adapter. */
    receivesWebhooks: z.boolean(),
    status: z.enum(['available', 'beta', 'planned']),
});
export const INTEGRATIONS = [
    // Repositories (docs/adr/0052): connected per project with an incoming webhook and a secret, no OAuth app in v1.
    { key: 'github', name: 'GitHub', category: 'repository', auth: 'webhook_only', feature: 'repos', capabilities: ['repo.push', 'repo.pull_request', 'repo.commit_link'], receivesWebhooks: true, status: 'available' },
    { key: 'bitbucket', name: 'Bitbucket', category: 'repository', auth: 'webhook_only', feature: 'repos', capabilities: ['repo.push', 'repo.pull_request', 'repo.commit_link'], receivesWebhooks: true, status: 'available' },
    { key: 'gitlab', name: 'GitLab', category: 'repository', auth: 'webhook_only', feature: 'repos', capabilities: ['repo.push', 'repo.pull_request', 'repo.commit_link'], receivesWebhooks: true, status: 'available' },
    // Phase 1 (docs/adr/0054): connect, push invoices, poll payments. Webhooks are not built yet.
    { key: 'xero', name: 'Xero', category: 'accounting', auth: 'oauth2', feature: 'invoicing', capabilities: ['invoice.sync'], receivesWebhooks: false, status: 'beta' },
    { key: 'stripe', name: 'Stripe', category: 'payments', auth: 'oauth2', feature: 'invoicing', capabilities: ['payment.collect'], receivesWebhooks: true, status: 'planned' },
    { key: 'slack_import', name: 'Slack import', category: 'import', auth: 'oauth2', feature: 'chat', capabilities: ['import.workspace'], receivesWebhooks: false, status: 'planned' },
    { key: 'asana_import', name: 'Asana import', category: 'import', auth: 'oauth2', feature: 'tasks', capabilities: ['import.workspace'], receivesWebhooks: false, status: 'planned' },
];
//# sourceMappingURL=integrations.js.map